import React, { useState } from 'react';
import type { TranscriptEntry } from './useAiakoAgentCall';
import { Avatar, MessageBubble } from './AiakoComponents';

interface AiakoSummaryProps {
  transcript: TranscriptEntry[];
  duration: string;
  onAskAgain: () => void;
}

function AskAgainButton({ onAskAgain }: { onAskAgain: () => void }) {
  return (
    <button
      onClick={onAskAgain}
      className="flex h-10 items-center justify-center gap-2 rounded-full px-6"
      style={{ backgroundColor: '#F9EFF2' }}
    >
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M8.527 3.605C8.742 3.021 9.591 3.021 9.807 3.605L11.064 7.017C11.338 7.761 11.941 8.347 12.706 8.614L16.216 9.836C16.817 10.045 16.817 10.871 16.216 11.080L12.706 12.303C11.941 12.570 11.338 13.155 11.064 13.900L9.807 17.312C9.591 17.896 8.742 17.896 8.527 17.312L7.269 13.900C6.995 13.155 6.393 12.570 5.627 12.303L2.117 11.080C1.517 10.871 1.517 10.045 2.117 9.836L5.627 8.614C6.393 8.347 6.995 7.761 7.269 7.017L8.527 3.605Z" fill="#CC0044"/>
      </svg>
      <p className="text-sm leading-5" style={{ color: '#CC0044', fontWeight: 450, letterSpacing: '-0.084px' }}>
        Ask Aiako again
      </p>
    </button>
  );
}

function ConversationEndedBadge({ duration }: { duration: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center justify-center rounded-full px-2 py-0.5" style={{ backgroundColor: '#EAEDEF' }}>
        <p className="text-xs leading-4" style={{ color: '#58595A', fontWeight: 450 }}>Conversation ended</p>
      </div>
      <p className="text-base font-semibold leading-6" style={{ color: '#161618', letterSpacing: '-0.16px' }}>{duration}</p>
    </div>
  );
}

function FeedbackButtons({ feedback, onSelect }: { feedback: 'yes' | 'no' | null; onSelect: (v: 'yes' | 'no') => void }) {
  return (
    <div className="flex items-center gap-2">
      {(['yes', 'no'] as const).map(val => (
        <button key={val} onClick={() => onSelect(val)}
          className="flex h-10 items-center justify-center gap-2 rounded-full border px-6"
          style={{ backgroundColor: feedback === val ? '#CC0044' : 'white', borderColor: feedback === val ? '#CC0044' : '#D9DDE0', color: feedback === val ? 'white' : '#161618' }}
        >
          {feedback === val && (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3.333 8.667L6 11.333L12.667 4.667" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          <p className="text-sm leading-5" style={{ fontWeight: 450, letterSpacing: '-0.084px' }}>{val === 'yes' ? 'Yes' : 'No'}</p>
        </button>
      ))}
    </div>
  );
}

const AiakoSummary: React.FC<AiakoSummaryProps> = ({ transcript, duration, onAskAgain }) => {
  const [feedback, setFeedback] = useState<'yes' | 'no' | null>(null);
  const [comment, setComment] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);

  const handleSendFeedback = () => {
    if (!feedback) return;
    setFeedbackSent(true);
  };

  const TranscriptPanel = () => (
    <>
      {transcript.map((entry, i) => <MessageBubble key={i} entry={entry} />)}
      <div className="flex items-center justify-center gap-2 pt-4">
        <p className="text-center text-sm leading-5" style={{ color: '#58595A', fontWeight: 450, letterSpacing: '-0.084px' }}>
          Your session has ended. Hope that helps!
        </p>
        <span className="text-base">👋</span>
      </div>
    </>
  );

  return (
    <div className="relative min-h-screen" style={{ backgroundColor: '#F6F8F8' }}>

      {/* Mobile */}
      <div className="md:hidden flex flex-col fixed inset-x-0 top-0" style={{ height: '100dvh', backgroundColor: '#F6F8F8' }}>
        <div className="bg-white flex items-center justify-center px-4 py-4 shrink-0">
          <h1 className="text-base font-semibold leading-6" style={{ color: '#161618', letterSpacing: '-0.16px' }}>Ask Aiako</h1>
        </div>

        <div className="flex-1 overflow-y-auto p-4" style={{ minHeight: 0 }}>
          <div className="flex flex-col gap-3">
            <TranscriptPanel />
            <div className="mb-[420px]" />
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 flex flex-col bg-white p-4 rounded-tl-[24px] rounded-tr-[24px]" style={{ boxShadow: '0px -2px 12px 0px rgba(26,24,23,0.1)' }}>
          {feedbackSent ? (
            <>
              <div className="flex justify-center mb-4"><ConversationEndedBadge duration={duration} /></div>
              <p className="text-center text-lg font-semibold leading-7 mb-1" style={{ color: '#161618' }}>Thank you for the feedback</p>
              <p className="text-center text-sm leading-5 mb-6" style={{ color: '#58595A', fontWeight: 450 }}>Your thoughts help Aiako improve.</p>
              <div className="flex justify-center"><AskAgainButton onAskAgain={onAskAgain} /></div>
            </>
          ) : (
            <>
              <div className="flex justify-center mb-4"><ConversationEndedBadge duration={duration} /></div>
              <div className="flex flex-col items-center gap-2 mb-4">
                <p className="text-center text-base font-semibold leading-6" style={{ color: '#161618', letterSpacing: '-0.16px' }}>Was the conversation helpful?</p>
                <FeedbackButtons feedback={feedback} onSelect={setFeedback} />
              </div>
              <div className="flex w-full flex-col items-center gap-2 mb-4">
                <textarea value={comment} onChange={e => setComment(e.target.value)}
                  placeholder="(Optional) Share your experience with Aiako"
                  className="h-16 w-full rounded-xl border px-4 py-2 text-sm leading-5 resize-none"
                  style={{ borderColor: '#D9DDE0', color: '#161618', fontWeight: 450, letterSpacing: '-0.084px' }}
                />
                <button onClick={handleSendFeedback} disabled={!feedback}
                  className="flex h-8 items-center justify-center gap-2 rounded-full border px-4 disabled:opacity-70"
                  style={{ backgroundColor: feedback ? 'white' : '#EAEDEF', borderColor: '#D9DDE0', color: feedback ? '#161618' : '#ADB1B4' }}
                >
                  <p className="text-sm leading-5" style={{ fontWeight: 450, letterSpacing: '-0.084px' }}>Send feedback</p>
                </button>
              </div>
              <div className="flex justify-center"><AskAgainButton onAskAgain={onAskAgain} /></div>
            </>
          )}
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden md:flex flex-col min-h-screen">
        <div className="w-full p-2">
          <div className="w-full max-w-[1200px] mx-auto p-2">
            <span className="text-xl font-bold leading-7" style={{ color: '#161618', letterSpacing: '-0.3px' }}>Aiako</span>
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center py-10 px-4">
          <div className="w-full flex justify-center" style={{ maxWidth: '1000px', height: '576px', borderRadius: '24px', boxShadow: '0px 9px 20px rgba(209,209,209,0.10), 0px 37px 37px rgba(209,209,209,0.09), 0px 82px 49px rgba(209,209,209,0.05), 0px 147px 59px rgba(209,209,209,0.01)' }}>

            {/* Left panel */}
            <div className="flex w-[360px] shrink-0 flex-col items-center justify-between rounded-l-3xl bg-white py-6 px-8">
              {feedbackSent ? (
                <>
                  <div className="flex flex-col items-center gap-2 pt-4">
                    <Avatar small />
                    <ConversationEndedBadge duration={duration} />
                  </div>
                  <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center">
                    <p className="text-xl font-semibold leading-7" style={{ color: '#161618', letterSpacing: '-0.2px' }}>Thank you for the feedback</p>
                    <p className="text-sm leading-5" style={{ color: '#58595A', fontWeight: 450, letterSpacing: '-0.084px' }}>Your thoughts help Aiako improve.</p>
                  </div>
                  <div className="flex w-full justify-center py-4"><AskAgainButton onAskAgain={onAskAgain} /></div>
                </>
              ) : (
                <>
                  <div className="flex flex-col items-center gap-2">
                    <Avatar small />
                    <ConversationEndedBadge duration={duration} />
                  </div>
                  <div className="flex flex-col items-center gap-6">
                    <div className="flex flex-col items-center gap-2">
                      <p className="text-center text-base font-semibold leading-6" style={{ color: '#161618', letterSpacing: '-0.16px' }}>Was the conversation helpful?</p>
                      <FeedbackButtons feedback={feedback} onSelect={setFeedback} />
                    </div>
                    <div className="flex w-[296px] flex-col items-center gap-2">
                      <textarea value={comment} onChange={e => setComment(e.target.value)}
                        placeholder="(Optional) Share your experience with Aiako"
                        className="h-24 w-full rounded-xl border px-4 py-2 text-sm leading-5 resize-none"
                        style={{ borderColor: '#D9DDE0', color: '#161618', fontWeight: 450, letterSpacing: '-0.084px' }}
                      />
                      <button onClick={handleSendFeedback} disabled={!feedback}
                        className="flex h-8 items-center justify-center gap-2 rounded-full border px-4 disabled:opacity-70"
                        style={{ backgroundColor: feedback ? 'white' : '#EAEDEF', borderColor: '#D9DDE0', color: feedback ? '#161618' : '#ADB1B4' }}
                      >
                        <p className="text-sm leading-5" style={{ fontWeight: 450, letterSpacing: '-0.084px' }}>Send feedback</p>
                      </button>
                    </div>
                  </div>
                  <div className="flex w-full justify-center py-4"><AskAgainButton onAskAgain={onAskAgain} /></div>
                </>
              )}
            </div>

            {/* Right panel */}
            <div className="flex-1 overflow-y-auto rounded-r-3xl p-6" style={{ backgroundColor: '#F6F8F8' }}>
              <div className="flex flex-col gap-3">
                <TranscriptPanel />
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm leading-5" style={{ color: '#58595A', fontWeight: 450, letterSpacing: '-0.084px' }}>
              Aiako provides general information only. Please verify important details.
            </p>
          </div>
          <div className="mt-4 flex items-center justify-center gap-1.5">
            <span className="text-xs leading-4" style={{ color: '#7E7F81', fontWeight: 450 }}>Powered by</span>
            <img src="/logos/Hupo_Logotype_Orange(noR).svg" alt="Hupo" className="h-3.5" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiakoSummary;
